import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import {
  Alert,
  AlertTitle,
  FormControl,
  GridLegacy,
  Typography,
} from '@mui/material';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { FieldValues, useForm } from 'react-hook-form';
import { HookFormToggle } from 'ui-component/HookFormComponents';
import { getCopy } from 'views/org/Preferences/copy';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { useGetPriceModelQuery } from 'store/slices/apiV1/purchasing';
import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import useSnackbar from 'hooks/useSnackbar';
import { PylonAction } from 'constants/pylonConstants';

const InventoryPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const { data: priceModel } = useGetPriceModelQuery(
    org?.priceModel.id || skipToken
  );
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const copy = getCopy(priceModel);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FieldValues>();
  const [isEditing, setIsEditing] = useState(false);
  const watchAllValues = watch();

  const useManagedInventory = org?.useManagedInventory;

  useEffect(() => {
    if (org) {
      reset({
        mslResetOnReceipt: org.mslResetOnReceipt,
        lcrInspectionOnReceipt: org.lcrInspectionOnReceipt,
        dimensionalInspectionOnReceipt: org.dimensionalInspectionOnReceipt,
        inspectionPhotoOnReceipt: org.inspectionPhotoOnReceipt,
      });
    }
  }, [org]);

  const onSubmit = async (payload: any) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!useManagedInventory && (
        <Alert
          severity="info"
          sx={{ mb: gridSpacing, background: 'white' }}
          action={
            <SegmentButton
              color="inherit"
              onClick={() => {
                if (window.Pylon) {
                  window.Pylon(PylonAction.show);
                }
              }}
            >
              Contact Cofactr Success
            </SegmentButton>
          }
        >
          <AlertTitle>
            Activate Managed by Cofactr Inventory Services
          </AlertTitle>
          Contact Cofactr Success to enabled Cofactr&apos;s warehousing,
          kitting, and inventory management services. Get access to state of the
          art secure, ESD-safe, climate controlled, ITAR registered storage and
          material management for your critical components, with in-house
          reeling, MSL reset bakes, and inspection services just a click away.
        </Alert>
      )}
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={gridSpacing}>
              {copy.toggles
                .filter(
                  (formField) =>
                    !formField.useManagedInventoryOnly || useManagedInventory
                )
                .map((formField) => (
                  <GridLegacy item xs={12} key={formField.name}>
                    <HookFormToggle
                      name={formField.name}
                      control={control}
                      label={formField.label}
                      disabled={!isEditing}
                    />
                    <Typography>
                      {watchAllValues[formField.name]
                        ? formField.description.true
                        : formField.description.false}
                    </Typography>
                  </GridLegacy>
                ))}
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.MANAGED_INVENTORY.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.MANAGED_INVENTORY.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Receiving Preferences"
      />
    </form>
  );
};

export default InventoryPreferences;
